import React, { useState, useEffect } from 'react'
import api from '../../services/api'
import { useAuthStore } from '../../stores/authStore.js'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { useTrusteesFooter, renderTrusteesHtml } from '../../components/reportTrusteesFooter'

function Neraca() {
  const { user } = useAuthStore()
  const [neracaData, setNeracaData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [day, setDay] = useState('')
  const [month, setMonth] = useState('')
  const [year, setYear] = useState('')
  const [asOfDate, setAsOfDate] = useState('')
  const [coopProfile, setCoopProfile] = useState(null)
  const [branchInfo, setBranchInfo] = useState(null)
  const [logoBase64, setLogoBase64] = useState('')

  const { trustees } = useTrusteesFooter(asOfDate, asOfDate)

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('id-ID', { day: '2-digit', month: '2-digit', year: 'numeric' })
  }

  const formatMonthDate = (dateString) => {
    if (!dateString) return '-'
    const d = new Date(dateString + 'T00:00:00')
    return d.toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })
  }

  useEffect(() => {
    const loadLogo = async () => {
      try {
        const response = await fetch('/logo.jpeg')
        const blob = await response.blob()
        const reader = new FileReader()
        reader.onloadend = () => setLogoBase64(reader.result)
        reader.readAsDataURL(blob)
      } catch (e) { console.log('Failed to load logo') }
    }
    loadLogo()
  }, [])

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '0'
    return new Intl.NumberFormat('id-ID').format(value)
  }

  useEffect(() => {
    const today = new Date()
    const y = today.getFullYear()
    const m = String(today.getMonth() + 1).padStart(2, '0')
    const d = String(today.getDate()).padStart(2, '0')
    setYear(String(y))
    setMonth(m)
    setDay(d)

    const fetchProfileInfo = async () => {
      try {
        const response = await api.get('/reports/profile-info')
        setCoopProfile(response.data?.data?.coopProfile)
        setBranchInfo(response.data?.data?.branchInfo)
      } catch (error) {
        console.error('Error fetching profile info:', error)
      }
    }
    fetchProfileInfo()
  }, [])

  useEffect(() => {
    if (year && month && day) {
      setAsOfDate(`${year}-${month}-${day}`)
    }
  }, [year, month, day])

  const fetchNeracaData = async () => {
    if (!asOfDate) return
    setLoading(true)
    try {
      const response = await api.get('/reports/neraca', { params: { asOfDate } })
      setNeracaData(response.data?.data)
    } catch (error) {
      console.error('Error fetching neraca data:', error)
      setNeracaData(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchNeracaData()
  }, [asOfDate])

  const renderTableHTML = (title, items, totals) => `
    <div class="section-header">${title}</div>
    <table>
      <thead><tr><th>Kode</th><th>Akun</th><th class="text-right">Saldo Sekarang</th><th class="text-right">Saldo Sebelumnya</th><th class="text-right">Mutasi</th></tr></thead>
      <tbody>
        ${(items || []).map(i => `<tr><td>${i.KodeAkun}</td><td>${i.NamaAkun}</td><td class="text-right">${formatCurrency(Math.abs(i.SaldoSekarang))}</td><td class="text-right">${formatCurrency(Math.abs(i.SaldoSebelumnya))}</td><td class="text-right">${formatCurrency(i.Mutasi)}</td></tr>`).join('')}
        <tr class="bg-gray"><td colspan="2">TOTAL ${title}</td><td class="text-right">${formatCurrency(totals.sekarang)}</td><td class="text-right">${formatCurrency(totals.sebelumnya)}</td><td class="text-right">${formatCurrency(totals.mutasi)}</td></tr>
      </tbody>
    </table>
  `

  const handlePrint = () => {
    if (!neracaData) return
    const printWindow = window.open('', '_blank')
    const htmlContent = `
      <html>
        <head>
          <title>Neraca - ${formatDate(asOfDate)}</title>
          <style>
            body { font-family: sans-serif; padding: 20px; font-size: 10px; }
            .kop-surat { text-align: center; border-bottom: 3px solid #000; padding-bottom: 15px; margin-bottom: 20px; }
            .kop-logo { width: 70px; height: auto; }
            .kop-nama { font-size: 18px; font-weight: bold; margin: 5px 0; }
            .kop-badan-hukum { font-size: 11px; margin: 3px 0; }
            .kop-alamat { font-size: 10px; margin: 2px 0; }
            .kop-kontak { font-size: 9px; margin: 2px 0; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 15px; font-size: 9px; }
            th, td { border: 1px solid #ccc; padding: 4px; }
            th { background: #f0f0f0; }
            .text-right { text-align: right; }
            .bg-section { background: #e8f4f8; font-weight: bold; }
            .bg-gray { background: #f2f2f2; font-weight: bold; }
          </style>
        </head>
        <body>
          <div class="kop-surat">
              <img src="${window.location.origin}/logo.jpeg" class="kop-logo" alt="Logo" />
              <div class="kop-nama">${coopProfile?.NamaKoperasi || 'KOPERASI SERBA USAHA'}</div>
              <div class="kop-badan-hukum">Badan Hukum No. : ${coopProfile?.NoBadanHukum || '-'}</div>
              <div class="kop-alamat">${coopProfile?.Alamat || ''}</div>
              <div class="kop-kontak">Telp: ${coopProfile?.Telepon || '-'}  |  Email: ${coopProfile?.Email || '-'}  |  NPWP: ${coopProfile?.NPWP || '-'}</div>
            </div>
          <h3 style="text-align:center">LAPORAN NERACA</h3>
          <p style="text-align:center">Per ${formatMonthDate(asOfDate)}</p>
          <p style="text-align:center">Periode Sebelumnya: ${formatMonthDate(neracaData.Periode?.Sebelumnya)}</p>
          ${renderTableHTML('AKTIVA', neracaData.Aktiva, { sekarang: neracaData.Totals.AktivaSekarang, sebelumnya: neracaData.Totals.AktivaSebelumnya, mutasi: neracaData.Totals.AktivaMutasi })}
          ${renderTableHTML('KEWAJIBAN', neracaData.Kewajiban, { sekarang: neracaData.Totals.KewajibanSekarang, sebelumnya: neracaData.Totals.KewajibanSebelumnya, mutasi: neracaData.Totals.KewajibanMutasi })}
          ${renderTableHTML('MODAL', neracaData.Modal, { sekarang: neracaData.Totals.ModalSekarang, sebelumnya: neracaData.Totals.ModalSebelumnya, mutasi: neracaData.Totals.ModalMutasi })}
          <table>
            <tr class="bg-gray"><td colspan="2">TOTAL KEWAJIBAN & MODAL</td><td class="text-right">${formatCurrency(neracaData.Totals.TotalPasivaSekarang)}</td><td class="text-right">${formatCurrency(neracaData.Totals.TotalPasivaSebelumnya)}</td><td class="text-right">${formatCurrency(neracaData.Totals.TotalPasivaMutasi)}</td></tr>
          </table>
        ${renderTrusteesHtml(trustees)}
        </body>
      </html>
    `
    printWindow.document.write(htmlContent)
    printWindow.document.close()
    printWindow.print()
  }

  const handleExportExcel = () => {
    if (!neracaData) return
    const wb = XLSX.utils.book_new()
    let wsData = [
      [coopProfile?.NamaKoperasi || 'Koperasi'],
      ['LAPORAN NERACA (KOMPARATIF)'],
      [`Per Tanggal: ${formatMonthDate(asOfDate)}`],
      [`Periode Sebelumnya: ${formatMonthDate(neracaData.Periode?.Sebelumnya)}`],
      [],
      ['SEKSI', 'KODE AKUN', 'NAMA AKUN', 'SALDO SEKARANG', 'SALDO SEBELUMNYA', 'MUTASI']
    ]

    const addSection = (title, items, totals) => {
      wsData.push([title])
      items.forEach(i => wsData.push(['', i.KodeAkun, i.NamaAkun, Math.abs(i.SaldoSekarang), Math.abs(i.SaldoSebelumnya), i.Mutasi]))
      wsData.push(['', '', `TOTAL ${title}`, totals.sekarang, totals.sebelumnya, totals.mutasi], [])
    }

    addSection('AKTIVA', neracaData.Aktiva, { sekarang: neracaData.Totals.AktivaSekarang, sebelumnya: neracaData.Totals.AktivaSebelumnya, mutasi: neracaData.Totals.AktivaMutasi })
    addSection('KEWAJIBAN', neracaData.Kewajiban, { sekarang: neracaData.Totals.KewajibanSekarang, sebelumnya: neracaData.Totals.KewajibanSebelumnya, mutasi: neracaData.Totals.KewajibanMutasi })
    addSection('MODAL', neracaData.Modal, { sekarang: neracaData.Totals.ModalSekarang, sebelumnya: neracaData.Totals.ModalSebelumnya, mutasi: neracaData.Totals.ModalMutasi })
    wsData.push(['', '', 'TOTAL KEWAJIBAN & MODAL', neracaData.Totals.TotalPasivaSekarang, neracaData.Totals.TotalPasivaSebelumnya, neracaData.Totals.TotalPasivaMutasi])

    const ws = XLSX.utils.aoa_to_sheet(wsData)
    ws['!cols'] = [{ wch: 15 }, { wch: 12 }, { wch: 30 }, { wch: 18 }, { wch: 18 }, { wch: 18 }]
    XLSX.utils.book_append_sheet(wb, ws, 'Neraca')
    XLSX.writeFile(wb, `Neraca_${asOfDate}.xlsx`)
  }

  const handleExportPDF = () => {
    const doc = new jsPDF({ orientation: 'l' })
    const pageWidth = doc.internal.pageSize.width

    try {
      doc.addImage(logoBase64, 'JPEG', 14, 5, 20, 20)
    } catch (e) { console.log('Logo PDF failed:', e.message) }

    doc.setFontSize(14).setFont(undefined, 'bold')
    doc.text(coopProfile?.NamaKoperasi || 'KOPERASI SERBA USAHA', pageWidth / 2, 10, { align: 'center' })
    doc.setFontSize(8).setFont(undefined, 'normal')
    doc.text(`Badan Hukum No. : ${coopProfile?.NoBadanHukum || '-'}`, pageWidth / 2, 15, { align: 'center' })
    doc.setFontSize(7).text(coopProfile?.Alamat || '', pageWidth / 2, 20, { align: 'center' })
    doc.text(`Telp: ${coopProfile?.Telepon || '-'}  |  Email: ${coopProfile?.Email || '-'}  |  NPWP: ${coopProfile?.NPWP || '-'}`, pageWidth / 2, 25, { align: 'center' })

    doc.setLineWidth(0.5)
    doc.line(10, 28, pageWidth - 10, 28)
    doc.setLineWidth(0.3)
    doc.line(10, 29, pageWidth - 10, 29)

    doc.setFontSize(10).setFont(undefined, 'bold')
    doc.text('LAPORAN NERACA (KOMPARATIF)', pageWidth / 2, 35, { align: 'center' })
    doc.setFontSize(7).setFont(undefined, 'normal')
    doc.text(`Per ${formatMonthDate(asOfDate)}  |  Periode Sebelumnya: ${formatMonthDate(neracaData.Periode?.Sebelumnya)}`, pageWidth / 2, 40, { align: 'center' })

    let finalY = 45
    const sections = [
      { title: 'AKTIVA', data: neracaData.Aktiva, totals: { s: neracaData.Totals.AktivaSekarang, p: neracaData.Totals.AktivaSebelumnya, m: neracaData.Totals.AktivaMutasi } },
      { title: 'KEWAJIBAN', data: neracaData.Kewajiban, totals: { s: neracaData.Totals.KewajibanSekarang, p: neracaData.Totals.KewajibanSebelumnya, m: neracaData.Totals.KewajibanMutasi } },
      { title: 'MODAL', data: neracaData.Modal, totals: { s: neracaData.Totals.ModalSekarang, p: neracaData.Totals.ModalSebelumnya, m: neracaData.Totals.ModalMutasi } }
    ]

    sections.forEach(sec => {
      const body = sec.data.map(i => [i.KodeAkun, i.NamaAkun, formatCurrency(Math.abs(i.SaldoSekarang)), formatCurrency(Math.abs(i.SaldoSebelumnya)), formatCurrency(i.Mutasi)])
      body.push([{ content: `TOTAL ${sec.title}`, colSpan: 2, styles: { fontStyle: 'bold' } },
        { content: formatCurrency(sec.totals.s), styles: { fontStyle: 'bold', halign: 'right' } },
        { content: formatCurrency(sec.totals.p), styles: { fontStyle: 'bold', halign: 'right' } },
        { content: formatCurrency(sec.totals.m), styles: { fontStyle: 'bold', halign: 'right' } }])

      autoTable(doc, {
        startY: finalY + 2,
        head: [[{ content: sec.title, colSpan: 5 }], ['Kode', 'Nama Akun', 'Saldo Sekarang', 'Saldo Sebelumnya', 'Mutasi']],
        body: body,
        theme: 'grid',
        styles: { fontSize: 7 },
        columnStyles: { 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' } },
        margin: { top: 30 }
      })
      finalY = doc.lastAutoTable.finalY + 5
    })

    const totalsBody = [
      ['', 'TOTAL KEWAJIBAN & MODAL', formatCurrency(neracaData.Totals.TotalPasivaSekarang), formatCurrency(neracaData.Totals.TotalPasivaSebelumnya), formatCurrency(neracaData.Totals.TotalPasivaMutasi)]
    ]
    autoTable(doc, {
      startY: finalY + 2,
      head: [[{ content: 'REKAPITULASI', colSpan: 5 }]],
      body: totalsBody,
      theme: 'grid',
      styles: { fontSize: 7, fontStyle: 'bold' },
      columnStyles: { 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' } }
    })

    if (trustees.length > 0) {
      doc.setFontSize(6)
      const midX = pageWidth / 2
      
      const pengurus = trustees.filter(t => t.Kelompok === 'Pengurus')
      const pengawas = trustees.filter(t => t.Kelompok === 'Pengawas')
      const pengurusSub = pengurus.filter(t => t.Jabatan === 'Anggota/Panitia Kredit')
      const pengurusRest = pengurus.filter(t => t.Jabatan !== 'Anggota/Panitia Kredit')

      let yL = doc.lastAutoTable.finalY + 8
      if (pengurus.length) {
        doc.setFont(undefined, 'bold')
        doc.text('PENGURUS:', 14, yL)
        yL += 3
        doc.setFont(undefined, 'normal')
        pengurusRest.forEach(t => {
          doc.text(`${t.Jabatan} : ${t.NamaLengkap}`, 19, yL)
          yL += 3
        })
        if (pengurusSub.length) {
          doc.text('Anggota/Panitia Kredit :', 19, yL)
          yL += 3
          pengurusSub.forEach((t, i) => {
            doc.text(`${i + 1}. ${t.NamaLengkap}`, 24, yL)
            yL += 3
          })
        }
      }

      let yR = doc.lastAutoTable.finalY + 8
      if (pengawas.length) {
        doc.setFont(undefined, 'bold')
        doc.text('PENGAWAS:', midX + 5, yR)
        yR += 3
        doc.setFont(undefined, 'normal')
        pengawas.forEach(t => {
          doc.text(`${t.Jabatan} : ${t.NamaLengkap}`, midX + 10, yR)
          yR += 3
        })
      }
    }

    doc.save(`Neraca_${asOfDate}.pdf`)
  }

  return (
    <div className="container-fluid p-3">
      <div className="card shadow">
        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
          <h3 className="card-title m-0">Laporan Neraca (Komparatif)</h3>
          <div className="btn-group">
            <button className="btn btn-sm btn-success" onClick={handleExportExcel} disabled={!neracaData || loading}>
              <i className="fas fa-file-excel mr-1"></i> Excel
            </button>
            <button className="btn btn-sm btn-danger" onClick={handleExportPDF} disabled={!neracaData || loading}>
              <i className="fas fa-file-pdf mr-1"></i> PDF
            </button>
            <button className="btn btn-sm btn-light" onClick={handlePrint} disabled={!neracaData || loading}>
              <i className="fas fa-print mr-1"></i> Cetak
            </button>
          </div>
        </div>

        <div className="card-body">
          <div className="row mb-4 bg-light p-3 rounded">
            <div className="col-md-6">
              <label className="small font-weight-bold">PER TANGGAL</label>
              <div className="d-flex">
                <select className="form-control form-control-sm mr-2" style={{ width: '70px' }} value={day} onChange={(e) => setDay(e.target.value)}>
                  {Array.from({ length: 31 }, (_, i) => <option key={i + 1} value={String(i + 1).padStart(2, '0')}>{i + 1}</option>)}
                </select>
                <select className="form-control form-control-sm mr-2" style={{ width: 'auto' }} value={month} onChange={(e) => setMonth(e.target.value)}>
                  <option value="01">Januari</option><option value="02">Februari</option><option value="03">Maret</option>
                  <option value="04">April</option><option value="05">Mei</option><option value="06">Juni</option>
                  <option value="07">Juli</option><option value="08">Agustus</option><option value="09">September</option>
                  <option value="10">Oktober</option><option value="11">November</option><option value="12">Desember</option>
                </select>
                <select className="form-control form-control-sm" style={{ width: '100px' }} value={year} onChange={(e) => setYear(e.target.value)}>
                  {Array.from({ length: 10 }, (_, i) => <option key={i} value={String(new Date().getFullYear() - 5 + i)}>{new Date().getFullYear() - 5 + i}</option>)}
                </select>
              </div>
              <small className="text-muted">Periode pembanding: {neracaData?.Periode?.Sebelumnya ? formatMonthDate(neracaData.Periode.Sebelumnya) : '-'}</small>
            </div>
          </div>

          {loading ? (
            <div className="text-center p-5"><div className="spinner-border text-primary"></div><p>Memuat Data...</p></div>
          ) : !neracaData ? (
            <div className="alert alert-warning">Data tidak ditemukan untuk tanggal ini.</div>
          ) : (
            <div className="table-responsive">
              <NeracaSection title="AKTIVA" items={neracaData.Aktiva}
                totals={{ sekarang: neracaData.Totals.AktivaSekarang, sebelumnya: neracaData.Totals.AktivaSebelumnya, mutasi: neracaData.Totals.AktivaMutasi }}
                format={formatCurrency} />
              <NeracaSection title="KEWAJIBAN" items={neracaData.Kewajiban}
                totals={{ sekarang: neracaData.Totals.KewajibanSekarang, sebelumnya: neracaData.Totals.KewajibanSebelumnya, mutasi: neracaData.Totals.KewajibanMutasi }}
                format={formatCurrency} />
              <NeracaSection title="MODAL" items={neracaData.Modal}
                totals={{ sekarang: neracaData.Totals.ModalSekarang, sebelumnya: neracaData.Totals.ModalSebelumnya, mutasi: neracaData.Totals.ModalMutasi }}
                format={formatCurrency} isLast
                grandTotals={{ sekarang: neracaData.Totals.TotalPasivaSekarang, sebelumnya: neracaData.Totals.TotalPasivaSebelumnya, mutasi: neracaData.Totals.TotalPasivaMutasi }} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const NeracaSection = ({ title, items, totals, format, isLast, grandTotals }) => (
  <>
    <h5 className="p-2 mt-3" style={{ backgroundColor: '#e8f4f8', borderLeft: '4px solid #17a2b8' }}>{title}</h5>
    <table className="table table-sm table-bordered table-hover">
      <thead className="thead-light">
        <tr>
          <th>Kode</th>
          <th>Nama Akun</th>
          <th className="text-right">Saldo Sekarang</th>
          <th className="text-right">Saldo Sebelumnya</th>
          <th className="text-right">Mutasi (+/-)</th>
        </tr>
      </thead>
      <tbody>
        {items?.map((a, idx) => (
          <tr key={idx}>
            <td>{a.KodeAkun}</td>
            <td>{a.NamaAkun}</td>
            <td className="text-right">{format(Math.abs(a.SaldoSekarang))}</td>
            <td className="text-right">{format(Math.abs(a.SaldoSebelumnya))}</td>
            <td className={`text-right font-weight-bold ${a.Mutasi >= 0 ? 'text-success' : 'text-danger'}`}>{format(a.Mutasi)}</td>
          </tr>
        ))}
        <tr className="font-weight-bold table-secondary">
          <td colSpan="2">TOTAL {title}</td>
          <td className="text-right">{format(totals.sekarang)}</td>
          <td className="text-right">{format(totals.sebelumnya)}</td>
          <td className="text-right">{format(totals.mutasi)}</td>
        </tr>
        {isLast && grandTotals && (
          <tr className="font-weight-bold table-info">
            <td colSpan="2">TOTAL KEWAJIBAN & MODAL</td>
            <td className="text-right">{format(grandTotals.sekarang)}</td>
            <td className="text-right">{format(grandTotals.sebelumnya)}</td>
            <td className="text-right">{format(grandTotals.mutasi)}</td>
          </tr>
        )}
      </tbody>
    </table>
  </>
)

export default Neraca